import { useEffect, useRef, useState } from 'react';
import { useAppStore } from 'kfone-frontend-core';
import { KFOneCookie } from 'kfone-frontend-core/enums';
import { getCookie } from 'utilities/helper';

export type SseConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

const RECONNECT_DELAY_MS = 5000;

/**
 * Match kf1AuthenticatedHttpClient token resolution:
 * AppStore → KFOne access-token cookie → local-dev sessionStorage.
 */
const resolveAuthToken = (): string | null => {
  const storeToken: string | undefined =
    useAppStore.getState().keycloakAuthState?.token;
  if (storeToken) {
    return storeToken;
  }
  const cookieToken: string | null = getCookie(KFOneCookie.accessToken);
  if (cookieToken) {
    return cookieToken;
  }
  return sessionStorage.getItem('authToken');
};

const parseSseBlocks = (buffer: string, onEvent: (data: unknown) => void): string => {
  const blocks: string[] = buffer.split('\n\n');
  const remainder: string = blocks.pop() ?? '';
  for (const block of blocks) {
    const dataLine: string | undefined = block
      .split('\n')
      .find((line: string) => line.startsWith('data:'));
    if (!dataLine) {
      continue;
    }
    const raw: string = dataLine.slice(5).trim();
    try {
      onEvent(JSON.parse(raw) as unknown);
    } catch {
      onEvent(raw);
    }
  }
  return remainder;
};

/**
 * Consumes a Server-Sent Events stream via fetch + Bearer auth.
 * Native EventSource cannot set Authorization headers.
 */
export const useSseStream = (
  url: string,
  onEvent: (data: unknown) => void,
  enabled = true,
): SseConnectionStatus => {
  const [status, setStatus] = useState<SseConnectionStatus>('disconnected');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !url) {
      setStatus('disconnected');
      return;
    }

    const abortController = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let connectTimer: ReturnType<typeof setTimeout> | undefined;
    let isActive = true;

    const connect = async (): Promise<void> => {
      if (!isActive) {
        return;
      }
      setStatus((previous: SseConnectionStatus) =>
        previous === 'disconnected' ? 'reconnecting' : previous,
      );
      const token: string | null = resolveAuthToken();
      if (!token) {
        console.warn('SSE stream: auth token missing, skipping connect');
      } else {
        try {
          const response: Response = await fetch(url, {
            method: 'GET',
            headers: {
              Accept: 'text/event-stream',
              Authorization: `Bearer ${token}`,
            },
            signal: abortController.signal,
          });
          if (!response.ok) {
            throw new Error(`SSE connection failed with status ${response.status}`);
          }
          if (!response.body) {
            throw new Error('SSE response body is empty');
          }
          setStatus('connected');
          const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (isActive) {
            const { done, value } = await reader.read();
            if (done) {
              break;
            }
            buffer += decoder.decode(value, { stream: true });
            buffer = parseSseBlocks(buffer, (data: unknown) => {
              onEventRef.current(data);
            });
          }
        } catch (error: unknown) {
          if (abortController.signal.aborted) {
            return;
          }
          console.error('SSE stream error:', error);
        }
      }
      if (!isActive || abortController.signal.aborted) {
        return;
      }
      setStatus('reconnecting');
      reconnectTimer = setTimeout(() => {
        void connect();
      }, RECONNECT_DELAY_MS);
    };

    // Defer so React StrictMode's first mount cleanup clears the timer before fetch starts.
    connectTimer = setTimeout(() => {
      void connect();
    }, 0);

    return () => {
      isActive = false;
      setStatus('disconnected');
      if (connectTimer !== undefined) {
        clearTimeout(connectTimer);
      }
      if (reconnectTimer !== undefined) {
        clearTimeout(reconnectTimer);
      }
      abortController.abort();
    };
  }, [url, enabled]);

  return status;
};
